import React, { useState } from 'react';
import {
    Box, Flex, Text, Badge, Icon, Image, Tooltip, Collapse, IconButton,
    HStack, VStack, Divider, Tag, TagLabel
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp, FaCheckCircle, FaTimesCircle, FaClipboardCheck, FaInfoCircle, FaBoxOpen, FaBookmark, FaHandshake, FaPen } from 'react-icons/fa';
import { GoPackage } from 'react-icons/go';
import { MdLocalShipping, MdCancel, MdPersonAdd, MdDoNotDisturb, MdMoneyOff } from 'react-icons/md';
import { BsFillPersonDashFill } from 'react-icons/bs';
import { IoMdCart } from 'react-icons/io';
import ProdutoImagem from '../ProdutoImagem';
import Notas from '../Notas';
import { CANAIS_CONTATO, mascaraTelefone, textoDesconto } from './negociacao';
import { formatarData } from '../comissoes/formato';

const rotuloCanal = (n) => (n?.canal_contato === 'outro' && n?.canal_contato_outro)
    ? n.canal_contato_outro
    : CANAIS_CONTATO.find((c) => c.value === n?.canal_contato)?.label;

// Resumo da negociacao (#1517) com o botao de registrar ou editar.
const BlocoNegociacao = ({ negociacao, podeEditar, onEditar }) => {
    const partes = negociacao ? [
        rotuloCanal(negociacao),
        [negociacao.cliente_nome, negociacao.cliente_telefone && mascaraTelefone(negociacao.cliente_telefone)].filter(Boolean).join(' · '),
        negociacao.data_negociacao && `Negociado em ${formatarData(negociacao.data_negociacao)}`,
        negociacao.cupom && `Cupom ${negociacao.cupom}`,
        textoDesconto(negociacao) && `Desconto ${textoDesconto(negociacao)}`,
    ].filter(Boolean) : [];

    return (
        <Flex
            px={{ base: 4, md: 5 }}
            py={2.5}
            borderTopWidth="1px"
            borderTopColor="gray.100"
            align="center"
            gap={2}
            flexWrap="wrap"
        >
            <HStack spacing={1.5} color="gray.500" flexShrink={0}>
                <Icon as={FaHandshake} boxSize={3.5} />
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">Negociação</Text>
            </HStack>
            {partes.length > 0 ? (
                partes.map((p, i) => (
                    <Tag key={i} size="sm" borderRadius="full" variant={i === 0 ? 'solid' : 'subtle'} colorScheme={i === 0 ? 'purple' : 'gray'}>
                        <TagLabel fontSize="11px">{p}</TagLabel>
                    </Tag>
                ))
            ) : (
                <Text fontSize="xs" color="gray.500">Não registrada</Text>
            )}
            {podeEditar && (
                <Tooltip label={partes.length ? 'Editar negociação' : 'Registrar negociação'} hasArrow>
                    <IconButton
                        ml="auto"
                        size="xs"
                        variant="ghost"
                        colorScheme="purple"
                        aria-label={partes.length ? 'Editar negociação' : 'Registrar negociação'}
                        icon={<Icon as={FaPen} />}
                        onClick={onEditar}
                    />
                </Tooltip>
            )}
        </Flex>
    );
};

const getLogoOrigem = (origem) => {
    switch (origem) {
        case 'Mercado Livre': return '/ml_logo_2.webp';
        case 'Mercado Shops': return '/ms_logo.webp';
        case 'Magalu': return '/magalu-logo.webp';
        case 'Amazon': return '/amazon-logo.webp';
        case 'TAGA': return '/taga_logo.jpg';
        case 'ECOMMERCE': return '/taga_logo.jpg';
        default: return '/taga_logo.jpg';
    }
};

const getStatusEnvioProps = (status) => {
    switch (status) {
        case 'CONFIRMADA': return { label: 'Confirmada', colorScheme: 'gray', icon: IoMdCart };
        case 'PARA ENVIAR': return { label: 'Para Enviar', colorScheme: 'yellow', icon: GoPackage };
        case 'EM TRÂNSITO': return { label: 'Em Trânsito', colorScheme: 'blue', icon: MdLocalShipping };
        case 'CONCLUÍDA': return { label: 'Concluída', colorScheme: 'green', icon: FaCheckCircle };
        case 'CANCELADA': return { label: 'Cancelada', colorScheme: 'red', icon: MdCancel };
        default: return { label: status, colorScheme: 'gray', icon: FaInfoCircle };
    }
};

const getStatusTagaProps = (statusTaga) => {
    switch (statusTaga) {
        case 1: return { label: 'Confirmada', colorScheme: 'green', icon: FaClipboardCheck };
        case 2: return { label: 'Cancelada', colorScheme: 'red', icon: FaTimesCircle };
        case 3: return { label: 'Pendente', colorScheme: 'yellow', icon: FaInfoCircle };
        default: return null;
    }
};

const getMarcadorProps = (marcador) => {
    switch (marcador) {
        case 1: return { label: 'Furo de Estoque', colorScheme: 'red', icon: FaBoxOpen };
        case 2: return { label: 'Pendências', colorScheme: 'yellow', icon: MdDoNotDisturb };
        case 3: return { label: 'Problema no Envio', colorScheme: 'gray', icon: MdLocalShipping };
        case 4: return { label: 'Erro de Preço', colorScheme: 'purple', icon: MdMoneyOff };
        case 5: return { label: 'Outros Problemas', colorScheme: 'blue', icon: FaBookmark };
        default: return null;
    }
};

const getImagemFornecedor = (fornecedor) => {
    if (fornecedor === 'MORELATE') return '/logo_morelate.webp';
    if (fornecedor === 'ISAPA') return '/logo_isapa.webp';
    if (fornecedor === 'BRCOMPANY') return '/logo_brcompany.webp';
    if (fornecedor === 'AUTOLATINA') return '/logo_autolatina.webp';
    if (fornecedor === 'LNG') return '/logo_lng.webp';
    if (fornecedor === 'CBA') return '/logo_cba.webp';
    if (fornecedor === 'MSAM') return '/logo_msam.webp';
    if (fornecedor === 'BLPARTS') return '/logo_blparts.webp';
    return '/logo_vannucci.webp';
};

const limitarCaracteres = (str, limite) => {
    if (str?.length && str.length > limite) {
        return str.slice(0, limite) + '...';
    }
    return str;
};

const calcularValorTotalItem = (quantidade, preco) => {
    const precoFormatado = parseFloat(String(preco || '0').replace(',', '.'));
    const valorTotal = (Number(quantidade) || 0) * precoFormatado;
    return valorTotal.toFixed(2).replace('.', ',');
};

const StatusBadge = ({ scheme, icon, label }) => (
    <Badge
        colorScheme={scheme}
        px={3}
        py={1.5}
        borderRadius="full"
        display="inline-flex"
        alignItems="center"
        gap={1.5}
        fontSize="11px"
        fontWeight="semibold"
        textTransform="none"
    >
        <Icon as={icon} boxSize={3} />
        {label}
    </Badge>
);

const CardPedido = ({
    pedido,
    onClickAtribuir,
    handleAddObs,
    handleApagarObs,
    textObs,
    handleChangeTextObs,
    negociacao,
    podeEditarNegociacao = false,
    onEditarNegociacao,
}) => {
    const [expandido, setExpandido] = useState(false);
    const [resumoExpandido, setResumoExpandido] = useState(false);

    const statusEnvio = getStatusEnvioProps(pedido.status);
    const statusTaga = getStatusTagaProps(pedido.status_taga);
    const marcador = getMarcadorProps(pedido.marcador);

    const produtoPrincipal = pedido.produtos?.[0];
    const totalProdutos = pedido.produtos?.length || 0;

    return (
        <Box
            bg="white"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="sm"
            overflow="hidden"
            transition="all 0.2s"
            _hover={{ boxShadow: 'md', borderColor: 'orange.200' }}
        >
            <Flex
                px={{ base: 4, md: 5 }}
                py={3}
                bg="gray.50"
                borderBottomWidth="1px"
                borderBottomColor="gray.100"
                gap={3}
                align="center"
                flexWrap="wrap"
            >
                <Flex
                    bg="white"
                    borderRadius="md"
                    boxShadow="xs"
                    w={11}
                    h={11}
                    align="center"
                    justify="center"
                    p={1.5}
                    flexShrink={0}
                    borderWidth="1px"
                    borderColor="gray.100"
                >
                    <Image
                        src={getLogoOrigem(pedido.origem)}
                        alt={pedido.origem}
                        maxW="100%"
                        maxH="100%"
                        objectFit="contain"
                    />
                </Flex>

                <VStack align="flex-start" spacing={0.5} flex="1" minW="180px">
                    <HStack spacing={2} align="baseline">
                        <Text fontWeight="bold" fontSize="md" color="gray.800">
                            #{pedido.id_taga}
                        </Text>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                            {pedido.data_hora}
                        </Text>
                    </HStack>
                    <HStack spacing={3} fontSize="xs" color="gray.500" flexWrap="wrap">
                        <Text>
                            ID Origem: <Text as="span" color="gray.700" fontWeight="semibold">{pedido.id}</Text>
                        </Text>
                        {pedido?.pack_id && (
                            <Text>
                                Pack: <Text as="span" color="gray.700" fontWeight="semibold">{pedido.pack_id}</Text>
                            </Text>
                        )}
                    </HStack>
                </VStack>

                <HStack spacing={2}>
                    <Notas
                        handleAddObs={handleAddObs}
                        idVenda={pedido.id_taga}
                        handleChangeTextObs={handleChangeTextObs}
                        textObs={textObs}
                        observacoes={pedido.observacoes}
                        handleApagarObs={handleApagarObs}
                    />
                    {pedido.vendedor ? (
                        <Tooltip label="Remover atribuição" hasArrow>
                            <IconButton
                                aria-label="Remover atribuição"
                                icon={<Icon as={BsFillPersonDashFill} />}
                                colorScheme="red"
                                variant="solid"
                                size="md"
                                onClick={() => onClickAtribuir(pedido)}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip label="Atribuir venda" hasArrow>
                            <IconButton
                                aria-label="Atribuir venda"
                                icon={<Icon as={MdPersonAdd} />}
                                colorScheme="green"
                                variant="solid"
                                size="md"
                                onClick={() => onClickAtribuir(pedido)}
                            />
                        </Tooltip>
                    )}
                </HStack>
            </Flex>

            {(statusEnvio || statusTaga || marcador) && (
                <Flex
                    px={{ base: 4, md: 5 }}
                    py={3}
                    gap={2}
                    flexWrap="wrap"
                    borderBottomWidth="1px"
                    borderBottomColor="gray.100"
                >
                    {statusEnvio && (
                        <StatusBadge scheme={statusEnvio.colorScheme} icon={statusEnvio.icon} label={statusEnvio.label} />
                    )}
                    {statusTaga && (
                        <StatusBadge scheme={statusTaga.colorScheme} icon={statusTaga.icon} label={`TAGA · ${statusTaga.label}`} />
                    )}
                    {marcador && (
                        <StatusBadge scheme={marcador.colorScheme} icon={marcador.icon} label={marcador.label} />
                    )}
                </Flex>
            )}

            <Flex
                px={{ base: 4, md: 5 }}
                py={4}
                gap={4}
                align="center"
                flexWrap={{ base: 'wrap', md: 'nowrap' }}
            >
                <Box flexShrink={0} position="relative">
                    {produtoPrincipal && (
                        <ProdutoImagem
                            produto={produtoPrincipal}
                            origem={pedido.origem}
                            width="84px"
                            height="84px"
                        />
                    )}
                    {totalProdutos > 1 && (
                        <Badge
                            position="absolute"
                            top="-6px"
                            right="-6px"
                            colorScheme="orange"
                            borderRadius="full"
                            fontSize="11px"
                            fontWeight="bold"
                            px={2}
                            py={0.5}
                            boxShadow="sm"
                        >
                            +{totalProdutos - 1}
                        </Badge>
                    )}
                </Box>

                <VStack align="flex-start" spacing={1.5} flex="1" minW={0}>
                    <Text fontWeight="semibold" fontSize="sm" color="gray.800" noOfLines={2} lineHeight="short">
                        {produtoPrincipal?.nome}
                    </Text>
                    <HStack spacing={3} fontSize="xs" color="gray.600" flexWrap="wrap">
                        <Text>
                            SKU: <Text as="span" fontWeight="semibold" color="gray.800">{produtoPrincipal?.id}</Text>
                        </Text>
                        {produtoPrincipal?.codigo && (
                            <Text>
                                Cód Global: <Text as="span" fontWeight="semibold" color="gray.800">{produtoPrincipal.codigo}</Text>
                            </Text>
                        )}
                    </HStack>
                    <HStack spacing={3} fontSize="xs" color="gray.600" flexWrap="wrap" pt={1}>
                        <Text>
                            Cliente: <Text as="span" fontWeight="semibold" color="gray.800">{limitarCaracteres(pedido?.dados_cliente?.nickname, 30) || '—'}</Text>
                        </Text>
                    </HStack>
                </VStack>

                <VStack align={{ base: 'flex-start', md: 'flex-end' }} spacing={2} flexShrink={0} minW="140px">
                    {pedido.vendedor ? (
                        <Tag colorScheme="blue" borderRadius="full" px={3} py={1} variant="solid">
                            <TagLabel fontSize="xs" fontWeight="bold">{pedido.vendedor.name}</TagLabel>
                        </Tag>
                    ) : (
                        <Tag colorScheme="gray" borderRadius="full" px={3} py={1} variant="subtle">
                            <TagLabel fontSize="xs">Sem vendedor</TagLabel>
                        </Tag>
                    )}
                    <Text fontSize="xl" fontWeight="bold" color="gray.800" lineHeight="none">
                        R$ {pedido.total}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                        {totalProdutos} {totalProdutos === 1 ? 'item' : 'itens'}
                    </Text>
                </VStack>
            </Flex>

            {pedido.vendedor && (
                <BlocoNegociacao
                    negociacao={negociacao}
                    podeEditar={podeEditarNegociacao}
                    onEditar={() => onEditarNegociacao?.(pedido)}
                />
            )}

            <Flex
                px={{ base: 4, md: 5 }}
                py={2.5}
                bg="gray.50"
                borderTopWidth="1px"
                borderTopColor="gray.100"
                cursor="pointer"
                onClick={() => setExpandido(!expandido)}
                _hover={{ bg: 'gray.100' }}
                align="center"
                justify="center"
                gap={2}
                color="gray.600"
            >
                <Icon as={expandido ? FaChevronUp : FaChevronDown} fontSize="11px" />
                <Text fontSize="sm" fontWeight="semibold">
                    {expandido ? 'Ocultar detalhes' : 'Ver detalhes'}
                </Text>
            </Flex>

            <Collapse in={expandido} animateOpacity>
                <Box bg="gray.50" px={{ base: 3, md: 5 }} py={4} borderTopWidth="1px" borderTopColor="gray.200">
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3}>
                        Itens do pedido
                    </Text>

                    <VStack align="stretch" spacing={3}>
                        {pedido.produtos?.map((produto) => (
                            <Box
                                key={produto.id}
                                bg="white"
                                borderRadius="lg"
                                p={{ base: 3, md: 4 }}
                                borderWidth="1px"
                                borderColor="gray.200"
                            >
                                <Flex gap={4} flexWrap={{ base: 'wrap', md: 'nowrap' }} align="center">
                                    <Box flexShrink={0}>
                                        <ProdutoImagem
                                            produto={produto}
                                            origem={pedido.origem}
                                            width="100px"
                                            height="100px"
                                        />
                                    </Box>

                                    <VStack align="flex-start" spacing={1} flex="1" minW={0}>
                                        <Text fontWeight="bold" fontSize="sm" color="gray.800" noOfLines={2}>
                                            {produto.nome}
                                        </Text>
                                        <HStack spacing={4} fontSize="xs" color="gray.600" flexWrap="wrap">
                                            <Text>SKU: <Text as="span" color="gray.800" fontWeight="semibold">{produto.id}</Text></Text>
                                            <Text>Cód Global: <Text as="span" color="gray.800" fontWeight="semibold">{produto.codigo || '—'}</Text></Text>
                                        </HStack>
                                        <HStack spacing={4} fontSize="xs" color="gray.600" flexWrap="wrap">
                                            <Text>Cód Fabricante: <Text as="span" color="gray.800">{produto.codigo_fabricante || '—'}</Text></Text>
                                            <Text>Cód Fornecedor: <Text as="span" color="gray.800">{produto.codigo_fornecedor || '—'}</Text></Text>
                                        </HStack>
                                        <HStack spacing={4} fontSize="xs" color="gray.600" flexWrap="wrap">
                                            <Text>Fabricante: <Text as="span" color="gray.800">{produto.fabricante || '—'}</Text></Text>
                                            <Text>ID Anúncio: <Text as="span" color="gray.800">{produto.id_ml || '—'}</Text></Text>
                                        </HStack>
                                    </VStack>

                                    {produto.fornecedor && (
                                        <Tooltip label={produto.fornecedor} hasArrow>
                                            <Box flexShrink={0}>
                                                <Image
                                                    src={getImagemFornecedor(produto.fornecedor)}
                                                    alt={produto.fornecedor}
                                                    maxW="80px"
                                                    maxH="50px"
                                                    objectFit="contain"
                                                />
                                            </Box>
                                        </Tooltip>
                                    )}

                                    <VStack align="flex-end" spacing={1} flexShrink={0} minW="110px">
                                        <Text fontSize="xs" color="gray.500">
                                            {produto.quantidade}× R$ {produto.preco}
                                        </Text>
                                        <Text fontSize="md" fontWeight="bold" color="gray.800">
                                            R$ {calcularValorTotalItem(produto.quantidade, produto.preco)}
                                        </Text>
                                    </VStack>
                                </Flex>
                            </Box>
                        ))}
                    </VStack>

                    <Box
                        mt={4}
                        bg="white"
                        borderRadius="lg"
                        overflow="hidden"
                        borderWidth="1px"
                        borderColor="gray.200"
                    >
                        <Flex
                            px={4}
                            py={2.5}
                            bg="gray.100"
                            cursor="pointer"
                            onClick={() => setResumoExpandido(!resumoExpandido)}
                            _hover={{ bg: 'gray.200' }}
                            align="center"
                            justify="space-between"
                        >
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                Resumo financeiro
                            </Text>
                            <Icon as={resumoExpandido ? FaChevronUp : FaChevronDown} fontSize="11px" color="gray.500" />
                        </Flex>

                        <Collapse in={resumoExpandido} animateOpacity>
                            <VStack align="stretch" spacing={0} px={4} py={2} divider={<Divider />}>
                                <Flex justify="space-between" py={2} fontSize="sm">
                                    <Text color="gray.600">Total de itens</Text>
                                    <Text fontWeight="semibold" color="gray.800">{pedido.total_itens}</Text>
                                </Flex>
                                <Flex justify="space-between" py={2} fontSize="sm">
                                    <Text color="gray.600">Subtotal</Text>
                                    <Text fontWeight="semibold" color="gray.800">R$ {pedido.subtotal}</Text>
                                </Flex>
                                <Flex justify="space-between" py={2} fontSize="sm">
                                    <Text color="gray.600">Desconto</Text>
                                    <Text fontWeight="semibold" color="red.500">- R$ {pedido.desconto}</Text>
                                </Flex>
                            </VStack>
                        </Collapse>

                        <Flex
                            px={4}
                            py={3}
                            bg="orange.50"
                            justify="space-between"
                            align="center"
                            borderTopWidth="1px"
                            borderTopColor="orange.100"
                        >
                            <Text fontSize="sm" fontWeight="bold" color="gray.700">Total</Text>
                            <Text fontSize="lg" fontWeight="bold" color="#F68633">R$ {pedido.total}</Text>
                        </Flex>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default CardPedido;
